import { FetchDetailOptions, FetchListOptions } from '~/api/_types';
import {
    deleteDemandRequest,
    fetchDemandDetailDraftRequest,
    fetchDemandDetailRequest,
    fetchDemandsRequest,
    patchDemandChangeStateRequest,
    patchDemandCreateOrderRequest,
    postDemandDraftRequest,
    postDemandRequest,
    putDemandDraftRequest,
    putDemandRequest,
} from '~/api/demands';
import { DemandDetail, DemandDraftDetail, DemandFormData, FetchDemandsResponse } from '~/api/demands/_types';
import { ApiSimpleResponseContext, FormHandler } from '~/plugins/apiClient/_types';
import { fetchAndStoreData, updateAndStoreFormData } from '../utils';
import router from '~/plugins/router';
import { useDemandDetailStore } from '~/stores/demandDetail';
import { useDemandDraftDetailStore } from '~/stores/demandDraftDetail';
import { DemandState } from '~/enums/_types';

export const fetchDemands = async (
    opt?: FetchListOptions
): Promise<ApiSimpleResponseContext<FetchDemandsResponse | null>> => {
    return await fetchDemandsRequest(opt);
};

export const fetchDemandDetail = async (
    id: number,
    opt?: FetchDetailOptions
): Promise<ApiSimpleResponseContext<DemandDetail | null>> => {
    return await fetchAndStoreData(
        router,
        'Id',
        id,
        getActiveDemand,
        async () => await fetchDemandDetailRequest(id),
        opt
    );
};

export const fetchDemandDraftDetail = async (
    draftId: number,
    opt?: FetchDetailOptions
): Promise<ApiSimpleResponseContext<DemandDraftDetail | null>> => {
    return await fetchAndStoreData(
        router,
        'Id',
        draftId,
        getActiveDemandDraft,
        async () => await fetchDemandDetailDraftRequest(draftId),
        opt
    );
};

export const sendDemand: FormHandler<DemandFormData, ApiSimpleResponseContext<DemandDetail | null>> = async (
    values,
    ctx
) => {
    return await updateAndStoreFormData(ctx, getActiveDemand, async () =>
        values.Id == null ? await postDemandRequest(values) : await putDemandRequest({ id: values.Id, data: values })
    );
};

export const saveDemandDraft: FormHandler<DemandFormData, ApiSimpleResponseContext<DemandDraftDetail | null>> = async (
    values,
    ctx
) => {
    return await updateAndStoreFormData(ctx, getActiveDemandDraft, async () =>
        values.Id == null
            ? await postDemandDraftRequest(values)
            : await putDemandDraftRequest({ id: values.Id, data: values })
    );
};

export const deleteDemandDraft = async (id: number): Promise<ApiSimpleResponseContext<null>> => {
    return await deleteDemandRequest(id);
};

export const changeStateDemand = async (
    id: number,
    newState: DemandState
): Promise<ApiSimpleResponseContext<DemandDetail | null>> => {
    const res = await patchDemandChangeStateRequest({ id, newState });
    const activeDemand = getActiveDemand();

    if (res.isSuccess) {
        activeDemand.value = res.data;
    }

    return res;
};

export const createOrderForDemand = async (
    id: number,
    changeState: boolean
): Promise<ApiSimpleResponseContext<DemandDetail | null>> => {
    const res = await patchDemandCreateOrderRequest({ id, changeState });
    const activeDemand = getActiveDemand();

    if (res.isSuccess) {
        activeDemand.value = res.data;
    }

    return res;
};

const getActiveDemand = () => storeToRefs(useDemandDetailStore()).activeDemand;
const getActiveDemandDraft = () => storeToRefs(useDemandDraftDetailStore()).activeDemandDraft;
